import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Button } from "@pondigitalsolutions/pon-component-library";

import { getLanguageFromPathname } from "@utils/lang";
import { DropdownMenu, DropdownMenuItem } from "./DropdownMenu";
import { useUpdatePreference } from "@hooks/useUpdatePreference";
import { languages } from "../constants";

interface LanguageDropdownProps {
  translatedSlug: string;
}

export const LanguageDropdown = ({ translatedSlug }: LanguageDropdownProps) => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { mutate: updatePreferences, isPending } = useUpdatePreference();

  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

  const currentLanguage =
    searchParams.get("_storyblok_lang") ||
    getLanguageFromPathname(location.pathname);

  const handleLanguageChange = (selectedLanguage: string) => {
    setIsLanguageDropdownOpen(false);

    if (selectedLanguage !== currentLanguage) {
      // Update the current URL to the translated slug.
      void navigate(translatedSlug, { replace: true });
    }

    // Add a timeout to ensure we change the language after the navigation.
    globalThis.setTimeout(() => {
      void i18n.changeLanguage(selectedLanguage).then(() => {
        // Update user language preference
        updatePreferences({
          languagePreference: selectedLanguage,
        });
      });
    }, 100);
  };

  return (
    <div className="relative">
      <Button
        id="language-switch"
        variant="secondary"
        size="xl"
        startIcon="Globe01"
        endIcon="ChevronDown"
        className="gap-1"
        aria-label={t("ControlCenter.language")}
        onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
        disabled={isPending}
      >
        {languages[currentLanguage as keyof typeof languages]}
      </Button>
      <DropdownMenu
        isOpen={isLanguageDropdownOpen}
        id="language-dropdown"
        className="top-auto bottom-12 left-0 w-fit"
      >
        <DropdownMenuItem
          key="language-nl"
          onClick={() => handleLanguageChange("nl")}
          className="group"
        >
          <DropdownIcon active={currentLanguage === "nl"} />
          Nederlands
        </DropdownMenuItem>
        <DropdownMenuItem
          key="language-en"
          onClick={() => handleLanguageChange("en")}
          className="group"
        >
          <DropdownIcon active={currentLanguage === "en"} />
          English
        </DropdownMenuItem>
      </DropdownMenu>
    </div>
  );
};

const DropdownIcon = ({ active }: { active: boolean }) => (
  <div
    className={twMerge(
      "flex h-5 w-5 items-center justify-center rounded-full border border-tertiary-200",
      "group-focus:ring-[3px] group-focus:ring-secondary-400 group-focus:ring-offset-1 group-focus:ring-offset-white",
      active ? "border-primary-500 bg-primary-500" : ""
    )}
  >
    {active && <div className="h-2 w-2 rounded-full bg-white" />}
  </div>
);
